// src/components/AddBookForm.js
import React, { useState } from 'react';
import { WEB_APP_URL, SECRET_TOKEN } from '../config';
import LoadingOverlay from './LoadingOverlay';
import book from '../assets/book.png';
import StarRatingInput from './StarRatingInput';
import { toast } from 'react-toastify';
import confetti from 'canvas-confetti';

const AddBookForm = ({ onBookAdded }) => {
  const [title, setTitle] = useState('');
  // Default rating is set to 3 (but can be cleared by clicking the same star)
  const [rating, setRating] = useState(3);
  const [comments, setComments] = useState('');
  const [coverUrl, setCoverUrl] = useState('');
  const [coverLoading, setCoverLoading] = useState(false);

  // Auto-fetch cover from Open Library, Google Books, then Bookcover API
  const fetchCover = async (searchTitle) => {
    if (!searchTitle || searchTitle.length < 3) {
      setCoverUrl('');
      return;
    }
    setCoverLoading(true);
    
    // Try Open Library first
    try {
      const res = await fetch(`https://openlibrary.org/search.json?q=${encodeURIComponent(searchTitle)}&limit=3`);
      const data = await res.json();
      const withCover = data.docs?.find(doc => doc.cover_i);
      if (withCover) {
        setCoverUrl(`https://covers.openlibrary.org/b/id/${withCover.cover_i}-M.jpg`);
        setCoverLoading(false);
        return;
      }
    } catch (err) {
      console.error('Open Library error:', err);
    }

    // Fallback to Google Books
    try {
      const res = await fetch(`https://www.googleapis.com/books/v1/volumes?q=${encodeURIComponent(searchTitle)}&maxResults=1`);
      const data = await res.json();
      const thumbnail = data.items?.[0]?.volumeInfo?.imageLinks?.thumbnail;
      if (thumbnail) {
        setCoverUrl(thumbnail.replace('http://', 'https://').replace('zoom=1', 'zoom=2'));
        setCoverLoading(false);
        return;
      }
    } catch (err) {
      console.error('Google Books error:', err);
    }

    setCoverUrl('');
    setCoverLoading(false);
  };

  // Debounced cover fetch
  React.useEffect(() => {
    const timer = setTimeout(() => {
      fetchCover(title);
    }, 500);
    return () => clearTimeout(timer);
  }, [title]);
  
  // Get today’s date in YYYY-MM-DD format.
  const getTodayDate = () => {
    const today = new Date();
    return today.toISOString().split('T')[0];
  };
  const [customDate, setCustomDate] = useState(getTodayDate());
  
  // Coupon toggle; default is true.
  const [couponRequested, setCouponRequested] = useState(true);
  
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  
  // Set couponReward to 2 if couponRequested is true, else 0.
  const couponReward = couponRequested ? 2 : 0;
  
  const handleSubmit = (e) => {
    e.preventDefault();
    setLoading(true);
    
    // Show an immediate loading toast.
    const toastId = toast.loading("Submitting book...");
    
    setTimeout(async () => {
      try {
        const response = await fetch(WEB_APP_URL, {
          method: 'POST',
          redirect: 'follow',
          headers: { "Content-Type": "text/plain;charset=utf-8" },
          body: JSON.stringify({
            action: 'addBook',
            title: title,
            rating: rating,
            comments: comments,
            date: customDate,
            coverUrl: coverUrl,
            token: SECRET_TOKEN,
            couponReward: couponReward,
          }),
        });
        const result = await response.json();
        if (result.success) {
          // 🎉 Confetti celebration!
          confetti({
            particleCount: 100,
            spread: 70,
            origin: { y: 0.6 }
          });
          
          onBookAdded();  // Trigger parent's refresh.
          // Reset form fields.
          setTitle('');
          setRating(3); // Reset back to default rating.
          setComments('');
          setCoverUrl('');
          setCustomDate(getTodayDate());
          setError(null);
          toast.update(toastId, {
            render: "Book submitted! Congratulations!",
            type: "success",
            isLoading: false,
            autoClose: 3000,
          });
        } else {
          setError(result.error || 'Error adding book');
          toast.update(toastId, {
            render: "Error adding book",
            type: "error",
            isLoading: false,
            autoClose: 3000,
          });
        }
      } catch (err) {
        console.error('Error posting book:', err);
        setError('Error posting book');
        toast.update(toastId, {
          render: "Error posting book",
          type: "error",
          isLoading: false,
          autoClose: 3000,
        });
      } finally {
        setLoading(false);
      }
    }, 0);
  };

  return (
    <div style={{ position: 'relative' }}>
      {loading && <LoadingOverlay image={book} message="Adding book..." />}
      <form onSubmit={handleSubmit} style={{ marginTop: '1em' }}>
        <div style={{ marginBottom: '1em' }}>
          <label>
            Book Title:
            <input
              type="text"
              value={title}
              onChange={e => setTitle(e.target.value)}
              required
              style={{ display: 'block', width: '100%', marginTop: '0.5em' }}
              disabled={loading}
            />
          </label>
        </div>
        <div style={{ marginBottom: '1em' }}>
          <p>Rating:</p>
          <StarRatingInput rating={rating} setRating={setRating} disabled={loading} />
        </div>
        <div style={{ marginBottom: '1em' }}>
          <label>
            Comments (optional):
            <textarea
              value={comments}
              onChange={e => setComments(e.target.value)}
              placeholder="Your comments here..."
              style={{ display: 'block', width: '100%', marginTop: '0.5em' }}
              disabled={loading}
            />
          </label>
        </div>
        {(coverUrl || coverLoading) && (
          <div style={{ marginBottom: '1em', textAlign: 'center' }}>
            {coverLoading ? (
              <p style={{ color: '#999', fontSize: '0.9em' }}>Finding cover...</p>
            ) : (
              <img 
                src={coverUrl} 
                alt="Cover preview" 
                style={{ maxHeight: '120px', borderRadius: '4px', boxShadow: '0 2px 8px rgba(0,0,0,0.15)' }}
                onError={(e) => e.target.style.display = 'none'}
              />
            )}
          </div>
        )}
        <div style={{ marginBottom: '1em' }}>
          <label>
            Read Date:
            <input
              type="date"
              value={customDate}
              onChange={e => setCustomDate(e.target.value)}
              style={{ display: 'block', width: '100%', marginTop: '0.5em' }}
              disabled={loading}
            />
          </label>
        </div>
        <div style={{ marginBottom: '1em', display: 'flex', alignItems: 'center' }}>
          <span>Request Coupon:</span>
          <div className="switch" style={{ marginLeft: '0.5em' }}>
            <input
              type="checkbox"
              id="couponSwitch"
              checked={couponRequested}
              onChange={e => setCouponRequested(e.target.checked)}
              disabled={loading}
            />
            <label className="slider" htmlFor="couponSwitch"></label>
          </div>
        </div>
        <button type="submit" style={{ marginTop: '1em' }} disabled={loading}>
          Add Book
        </button>
        {error && <p style={{ color: 'red' }}>{error}</p>}
      </form>
    </div>
  );
};

export default AddBookForm;
